import { useState, useEffect, useMemo } from "react";
import type { Product } from "@/types/product";

interface Props {
  product: Product;
}

// Deterministic seed from product ID so numbers are stable per product
function hashInt(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(31, h) + str.charCodeAt(i);
  }
  return Math.abs(h);
}

export default function SocialProofBar({ product }: Props) {
  const seed = useMemo(() => hashInt(product.id), [product.id]);

  const baseViewers = useMemo(() => (seed % 29) + 7, [seed]);   // 7–35
  const soldCount   = useMemo(() => (seed % 44) + 14, [seed]);  // 14–57
  const stockLeft   = useMemo(() => (seed % 7) + 3, [seed]);    // 3–9

  const [viewers, setViewers] = useState(baseViewers);

  // Slowly tick viewers up/down to feel live
  useEffect(() => {
    const interval = setInterval(() => {
      setViewers((v) => {
        const delta = Math.random() < 0.5 ? 1 : -1;
        return Math.max(baseViewers - 3, Math.min(baseViewers + 7, v + delta));
      });
    }, 4800);
    return () => clearInterval(interval);
  }, [baseViewers]);

  if (!product.inStock) return null;

  const showLowStock = stockLeft <= 6;

  return (
    <div className="flex flex-col gap-2 py-3 border-t border-gray-200/70 mt-1">
      {/* Live viewers */}
      <div className="flex items-center gap-2">
        <span className="relative flex h-2 w-2 shrink-0">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
        </span>
        <span className="text-xs text-gray-500">
          <span className="font-semibold text-gray-700">{viewers} people</span> viewing this right now
        </span>
      </div>

      {/* Sold count */}
      <div className="flex items-center gap-2">
        <div className="w-4 h-4 flex items-center justify-center shrink-0">
          <i className="ri-fire-line text-orange-400 text-xs"></i>
        </div>
        <span className="text-xs text-gray-500">
          <span className="font-semibold text-orange-600">{soldCount} sold</span> in the last 48 hours
        </span>
      </div>

      {/* Low stock warning */}
      {showLowStock && (
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 flex items-center justify-center shrink-0">
            <i className="ri-error-warning-line text-amber-500 text-xs"></i>
          </div>
          <span className="text-xs font-semibold text-amber-600">
            Only {stockLeft} left in stock — order soon
          </span>
        </div>
      )}
    </div>
  );
}
